import React from 'react';
import { Card } from '../../CompositeComponents/Card/Card';
import { Badge } from '../../BasicComponents/Badge/Badge';
import { Button } from '../../BasicComponents/Button/Button';
import type { ReservationCardProps, ReservationStatus } from './types';

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<ReservationStatus, { label: string; variant: string; bgClass: string }> = {
  confirmed:  { label: 'Confirmed',  variant: 'active',   bgClass: 'bg-brand-50 dark:bg-brand-900/20'    },
  pending:    { label: 'Pending',    variant: 'pending',  bgClass: 'bg-warning-50 dark:bg-warning-900/20' },
  arrived:    { label: 'Arrived',    variant: 'active',   bgClass: 'bg-success-50 dark:bg-success-900/20' },
  cancelled:  { label: 'Cancelled',  variant: 'inactive', bgClass: 'bg-neutral-50 dark:bg-neutral-800'   },
  no_show:    { label: 'No Show',    variant: 'danger',   bgClass: 'bg-danger-50 dark:bg-danger-900/20'   },
};

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// ─── ReservationCard ──────────────────────────────────────────────────────────

export function ReservationCard({
  id,
  guestName,
  partySize,
  phone,
  reservedAt,
  tableName,
  status,
  specialRequests,
  onConfirm,
  onSeat,
  onCancel,
  className,
}: ReservationCardProps) {
  const config = STATUS_CONFIG[status];

  return (
    <Card
      variant="outlined"
      padding="md"
      aria-label={`Reservation for ${guestName} at ${formatDateTime(reservedAt)}, ${config.label}`}
      className={[config.bgClass, className ?? ''].filter(Boolean).join(' ')}
    >
      <article className="flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-bold text-neutral-800 dark:text-neutral-100">{guestName}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {partySize} guest{partySize !== 1 ? 's' : ''} · {phone}
            </p>
          </div>
          <Badge variant={config.variant as any} size="sm">{config.label}</Badge>
        </div>

        {/* Time + table */}
        <div className="flex items-center gap-4 text-xs text-neutral-600 dark:text-neutral-400">
          <time dateTime={reservedAt} className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {formatDateTime(reservedAt)}
          </time>
          {tableName && (
            <span className="flex items-center gap-1">
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              </svg>
              {tableName}
            </span>
          )}
        </div>

        {/* Special requests */}
        {specialRequests && (
          <p className="text-xs italic text-neutral-500 dark:text-neutral-400 border-l-2 border-neutral-300 dark:border-neutral-600 pl-2">
            "{specialRequests}"
          </p>
        )}

        {/* Actions */}
        {(onConfirm || onSeat || onCancel) && status !== 'cancelled' && status !== 'no_show' && (
          <div className="flex gap-2 pt-1">
            {onConfirm && status === 'pending' && (
              <Button variant="primary" size="sm" onClick={() => onConfirm(id)} aria-label={`Confirm reservation for ${guestName}`}>
                Confirm
              </Button>
            )}
            {onSeat && status === 'confirmed' && (
              <Button variant="primary" size="sm" onClick={() => onSeat(id)} aria-label={`Seat ${guestName}`}>
                Seat
              </Button>
            )}
            {onCancel && status !== 'arrived' && (
              <Button variant="ghost" size="sm" onClick={() => onCancel(id)} aria-label={`Cancel reservation for ${guestName}`}>
                Cancel
              </Button>
            )}
          </div>
        )}
      </article>
    </Card>
  );
}
